import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Briefcase, Loader2, ChevronDown, ChevronRight, ShieldCheck, AlertTriangle,
  Lightbulb, TrendingUp, Inbox, CheckCircle2, ExternalLink, Send, Scale,
  Target, ShieldAlert, FileWarning, Activity, ScanSearch, RefreshCw, HeartPulse,
  CalendarClock, Split, Link2, Layers, GitCompareArrows, Eye, Undo2,
  FlaskConical, Rocket, RotateCcw, History, Clock,
} from 'lucide-react';
import { useCan } from '@/lib/permissions';

const BROWSER_TZ = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; } })();
const TZ_LIST = Array.from(new Set([BROWSER_TZ, 'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'Europe/London', 'Europe/Paris', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney']));
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const RES_PAGE = 25;
const PILOT_LABEL = { running: 'Running', promoted: 'Promoted', rolled_back: 'Rolled back', no_data: 'No data' };
const PILOT_COLOR = {
  running: 'text-sky-700 dark:text-sky-300 border-sky-500/30 bg-sky-500/10',
  promoted: 'text-emerald-700 dark:text-emerald-300 border-emerald-500/40 bg-emerald-500/10',
  rolled_back: 'text-muted-foreground border-border/40',
  no_data: 'text-muted-foreground border-border/40',
};

const OBLIGATION_LABEL = {
  advisory: 'Advisory', best_practice: 'Best practice', optimization: 'Optimization',
  policy_required: 'Policy required', compliance_required: 'Compliance required',
  security_required: 'Security required', administrative: 'Administrative',
  time_sensitive: 'Time-sensitive',
};
const EVIDENCE_LABEL = {
  descriptive: 'Descriptive', associative: 'Associative', predictive: 'Predictive',
  quasi_causal: 'Quasi-causal', causal: 'Causal', policy_based: 'Policy-based',
  expert_entered: 'Expert-entered',
};
const FAMILY_LABEL = {
  business_decision: 'Business decision', analytics_content: 'Analytics content',
  semantic_data: 'Semantic / data', monitoring: 'Monitoring',
};
const LIFECYCLE_LABEL = {
  published: 'Published', under_review: 'Under review', approved: 'Approved',
  in_progress: 'In progress', dismissed: 'Dismissed', deferred: 'Deferred',
  exception_granted: 'Exception granted', monitoring_outcome: 'Measuring outcome',
  successful: 'Successful', unsuccessful: 'Unsuccessful', inconclusive: 'Inconclusive',
};
const OUTCOME_LABEL = {
  monitoring: 'Measuring', successful: 'Successful', unsuccessful: 'Unsuccessful',
  inconclusive: 'Inconclusive', no_data: 'No data',
};
const OUTCOME_COLOR = {
  monitoring: 'text-sky-700 dark:text-sky-300 border-sky-500/30 bg-sky-500/10',
  successful: 'text-emerald-700 dark:text-emerald-300 border-emerald-500/40 bg-emerald-500/10',
  unsuccessful: 'text-rose-700 dark:text-rose-300 border-rose-500/40 bg-rose-500/10',
  inconclusive: 'text-amber-700 dark:text-amber-300 border-amber-500/40 bg-amber-500/10',
  no_data: 'text-muted-foreground border-border/40',
};
const CONF_COLOR = {
  high: 'text-emerald-600 dark:text-emerald-400 border-emerald-500/30 bg-emerald-500/10',
  medium: 'text-amber-600 dark:text-amber-400 border-amber-500/30 bg-amber-500/10',
  low: 'text-rose-600 dark:text-rose-400 border-rose-500/30 bg-rose-500/10',
};
const OPTION_ICON = { immediate: Send, limited: Scale, investigate: Lightbulb, monitor: TrendingUp, no_action: AlertTriangle };

function Badge({ children, className = '', testid }) {
  return <span data-testid={testid} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] border ${className}`}>{children}</span>;
}

function RecCard({ rec, selectMode, selected, onToggleSelect, onCompare, onResolved }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [rankOpen, setRankOpen] = useState(false);
  const [approvalId, setApprovalId] = useState(rec.approval_id || null);
  const [routing, setRouting] = useState(false);
  const [exc, setExc] = useState(rec.exception || null);
  const [excStatus, setExcStatus] = useState(rec.exception_status || null);
  const [excDialog, setExcDialog] = useState(false);
  const [excReason, setExcReason] = useState('');
  const [excExpiry, setExcExpiry] = useState('');
  const [excBusy, setExcBusy] = useState(false);
  const [outcome, setOutcome] = useState(rec.outcome || null);
  const [reval, setReval] = useState(rec.revalidation || null);
  const [busyC, setBusyC] = useState(false);
  const [pendingEvent, setPendingEvent] = useState('');
  const [causeConf, setCauseConf] = useState('medium');
  const [pilot, setPilot] = useState(rec.pilot || null);
  const [pilotDialog, setPilotDialog] = useState(false);
  const [pilotName, setPilotName] = useState('');
  const [pilotScope, setPilotScope] = useState('');
  const status = rec.approval_status || 'none';
  const isContent = rec.source === 'content_intel';

  const trackOutcome = async () => {
    setBusyC(true);
    try {
      const r = await api.startOutcome(rec.rec_id);
      setOutcome({ outcome_id: r.outcome_id, status: r.status, baseline: r.baseline, target: r.target, member: r.member, latest: null });
      toast.success('Outcome tracking started — baseline captured.');
    } catch (e) { toast.error(e.message || 'Could not start outcome'); }
    finally { setBusyC(false); }
  };
  const measureNow = async () => {
    if (!outcome?.outcome_id) return;
    setBusyC(true);
    try {
      const r = await api.measureOutcome(outcome.outcome_id);
      const ms = r.measurements || [];
      setOutcome({ outcome_id: r.outcome_id, status: r.status, baseline: r.baseline, target: r.target, member: r.member, latest: ms[ms.length - 1] || null });
      toast.success('Outcome measured.');
    } catch (e) { toast.error(e.message || 'Could not measure outcome'); }
    finally { setBusyC(false); }
  };
  const revalidate = async () => {
    setBusyC(true);
    try {
      const r = await api.revalidateRec(rec.rec_id);
      setReval(r);
      toast.success(r.status === 'condition_cleared' ? 'The condition has cleared.' : 'The condition still holds.');
    } catch (e) { toast.error(e.message || 'Could not revalidate'); }
    finally { setBusyC(false); }
  };
  const attributeEvent = async (eventId, confidence = 'medium') => {
    if (!outcome?.outcome_id) return;
    setBusyC(true);
    try {
      const r = await api.attributeOutcomeEvent(outcome.outcome_id, eventId, confidence);
      setOutcome({ ...outcome, attributed_event: r.attributed_event });
      toast.success(eventId ? 'Marked likely cause.' : 'Cleared attribution.');
    } catch (e) { toast.error(e.message || 'Could not attribute'); }
    finally { setBusyC(false); }
  };
  const markCause = () => { if (pendingEvent) { attributeEvent(pendingEvent, causeConf); setPendingEvent(''); } };
  const reviveRecFn = async () => {
    setBusyC(true);
    try { await api.reviveRec(rec.rec_id); toast.success('Recommendation revived.'); onResolved && onResolved(); }
    catch (e) { toast.error(e.message || 'Could not revive'); }
    finally { setBusyC(false); }
  };
  const doStartPilot = async () => {
    if (!pilotName.trim()) { toast.error('Name the pilot'); return; }
    setBusyC(true);
    try {
      const r = await api.startPilot(rec.rec_id, pilotName.trim(), pilotScope.trim());
      setPilot({ pilot_id: r.pilot_id, status: r.status, name: r.name, baseline: r.baseline, latest: null });
      setPilotDialog(false); setPilotName(''); setPilotScope('');
      toast.success('Pilot started — baseline captured.');
    } catch (e) { toast.error(e.message || 'Could not start pilot'); }
    finally { setBusyC(false); }
  };
  const measurePilotFn = async () => {
    if (!pilot?.pilot_id) return;
    setBusyC(true);
    try {
      const r = await api.measurePilot(pilot.pilot_id);
      const pm = r.measurements || [];
      setPilot({ pilot_id: r.pilot_id, status: r.status, name: r.name, baseline: r.baseline, latest: pm[pm.length - 1] || null });
      toast.success('Pilot measured.');
    } catch (e) { toast.error(e.message || 'Could not measure pilot'); }
    finally { setBusyC(false); }
  };
  const decidePilotFn = async (decision) => {
    if (!pilot?.pilot_id) return;
    setBusyC(true);
    try {
      const r = await api.decidePilot(pilot.pilot_id, decision);
      setPilot({ ...pilot, status: r.status });
      toast.success(decision === 'promote' ? 'Pilot promoted — routed to approval.' : 'Pilot rolled back.');
      onResolved && onResolved();
    } catch (e) { toast.error(e.message || 'Could not update pilot'); }
    finally { setBusyC(false); }
  };

  const route = async () => {
    setRouting(true);
    try {
      const res = await api.submitForApproval(rec.rec_id);
      if (res?.approval_id) setApprovalId(res.approval_id);
      toast.success('Routed to approval — pending a named approver (four-eyes).');
    } catch (e) {
      if (e.status === 409 || (e.message || '').includes('already exists')) {
        const id = (e.detail && typeof e.detail === 'object' && e.detail.approval_id) || null;
        if (id) setApprovalId(id);
        toast.info('An approval is already open for this recommendation.');
      } else { toast.error(e.message || 'Could not route to approval'); }
    } finally { setRouting(false); }
  };

  const submitException = async () => {
    if (!excReason.trim()) { toast.error('A reason is required'); return; }
    setExcBusy(true);
    try {
      const r = await api.requestException(rec.rec_id, excReason.trim(), excExpiry);
      setExc(r); setExcStatus('pending'); setExcDialog(false); setExcReason(''); setExcExpiry('');
      toast.success('Exception requested — needs a second approver to grant.');
    } catch (e) {
      if (e.status === 409) { toast.info('An exception is already open.'); setExcStatus('pending'); setExcDialog(false); }
      else { toast.error(e.message || 'Could not request exception'); }
    } finally { setExcBusy(false); }
  };

  const decideExc = async (decision) => {
    if (!exc?.exception_id) return;
    setExcBusy(true);
    try {
      await api.decideException(exc.exception_id, decision);
      setExcStatus(decision);
      toast.success(decision === 'granted' ? 'Exception granted' : 'Exception denied');
    } catch (e) { toast.error(e.message || 'Could not decide exception'); }
    finally { setExcBusy(false); }
  };

  const req = rec.required;
  const oblColor = req ? 'text-rose-700 dark:text-rose-300 border-rose-500/40 bg-rose-500/10'
    : 'text-violet-700 dark:text-violet-300 border-violet-500/30 bg-violet-500/10';

  return (
    <div className="bg-card/60 border border-border/40 rounded-2xl p-4" data-testid={`hub-rec-${rec.rec_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          {selectMode && (
            <input type="checkbox" checked={!!selected} onChange={() => onToggleSelect(rec.rec_id)}
              className="mt-1 accent-violet-500 flex-shrink-0" data-testid={`hub-select-${rec.rec_id}`} />
          )}
          <div className="min-w-0">
          <div className="font-heading font-semibold text-foreground flex items-center gap-1.5">
            {req ? <AlertTriangle className="w-4 h-4 text-rose-500 flex-shrink-0" /> : <ShieldCheck className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
            <span className="truncate">{rec.title}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">{rec.action}</p>
          {isContent && rec.rationale && <p className="text-[11px] text-muted-foreground/80 mt-1 italic">{rec.rationale}</p>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <button onClick={() => setRankOpen(!rankOpen)} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-700 dark:text-violet-300 border border-violet-500/30" data-testid={`hub-rank-${rec.rec_id}`} title="Why ranked">
            rank {(rec.rank?.score ?? 0).toFixed(2)} <ChevronDown className={`w-3 h-3 transition-transform ${rankOpen ? 'rotate-180' : ''}`} />
          </button>
          <span className="text-[10px] text-muted-foreground">{LIFECYCLE_LABEL[rec.lifecycle_state] || rec.lifecycle_state}</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mt-2">
        <Badge className="text-muted-foreground border-border/40">{FAMILY_LABEL[rec.family] || rec.family}</Badge>
        <Badge className={oblColor} testid={`hub-obligation-${rec.rec_id}`}>{OBLIGATION_LABEL[rec.obligation] || rec.obligation}{req ? ' • required' : ''}</Badge>
        {rec.material && <Badge className="text-rose-600 dark:text-rose-400 border-rose-500/30 bg-rose-500/10">Material</Badge>}
        <Badge className="text-muted-foreground border-border/40 capitalize">{rec.priority} priority</Badge>
        {rec.initiative && (
          <Badge className="text-violet-700 dark:text-violet-300 border-violet-500/30 bg-violet-500/10" testid={`hub-initiative-${rec.rec_id}`}>
            <Layers className="w-3 h-3" /> {rec.initiative.name}
          </Badge>
        )}
        {rec.impact?.dollars_display && rec.impact.dollars_display !== '—' && (
          <Badge className="text-foreground border-border/40">{rec.impact.points_display} ≈ {rec.impact.dollars_display}</Badge>
        )}
      </div>

      {/* Evidence strength + honesty note */}
      <div className="mt-2 flex items-start gap-1.5 text-[11px]">
        <Badge className="text-sky-700 dark:text-sky-300 border-sky-500/30 bg-sky-500/10" testid={`hub-evidence-${rec.rec_id}`}>Evidence: {EVIDENCE_LABEL[rec.evidence_level] || rec.evidence_level}</Badge>
        {rec.evidence_note && <span className="text-muted-foreground italic">{rec.evidence_note}</span>}
      </div>

      {/* Confidence dimensions */}
      {rec.confidence && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`hub-confidence-${rec.rec_id}`}>
          {Object.entries(rec.confidence).map(([k, v]) => (
            <Badge key={k} className={CONF_COLOR[v] || 'border-border/40 text-muted-foreground'}>
              {k.replace(/_/g, ' ')}: {v}
            </Badge>
          ))}
        </div>
      )}

      {/* Strategic alignment + guardrail flags */}
      {((rec.objective_alignment || []).length > 0 || (rec.guardrail_flags || []).length > 0) && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`hub-alignment-${rec.rec_id}`}>
          {(rec.objective_alignment || []).map((o) => (
            <Badge key={o.objective_id} className="text-violet-700 dark:text-violet-300 border-violet-500/30 bg-violet-500/10">
              <Target className="w-3 h-3" /> {o.name}
            </Badge>
          ))}
          {(rec.guardrail_flags || []).map((g) => (
            <Badge key={g.objective_id} className="text-rose-700 dark:text-rose-300 border-rose-500/40 bg-rose-500/10" testid={`hub-guardrail-${rec.rec_id}`}>
              <ShieldAlert className="w-3 h-3" /> may affect: {g.name}
            </Badge>
          ))}
        </div>
      )}

      {/* Conflict + dependency (Phase E) */}
      {(rec.conflict_reason || (rec.related_ids || []).length > 0) && (
        <div className="mt-2 flex flex-wrap items-start gap-1.5" data-testid={`hub-interactions-${rec.rec_id}`}>
          {rec.conflict_reason && (
            <Badge className="text-amber-700 dark:text-amber-300 border-amber-500/40 bg-amber-500/10" testid={`hub-conflict-${rec.rec_id}`}>
              <Split className="w-3 h-3" /> Conflict
            </Badge>
          )}
          {(rec.related_ids || []).length > 0 && (
            <Badge className="text-sky-700 dark:text-sky-300 border-sky-500/30 bg-sky-500/10" testid={`hub-related-${rec.rec_id}`}>
              <Link2 className="w-3 h-3" /> Linked to {rec.related_ids.length} on this segment
            </Badge>
          )}
          {rec.conflict_reason && <span className="text-[11px] text-muted-foreground italic">{rec.conflict_reason}</span>}
          {rec.resolution?.status === 'chosen' ? (
            <Badge className="text-emerald-700 dark:text-emerald-300 border-emerald-500/40 bg-emerald-500/10" testid={`hub-resolution-${rec.rec_id}`}>
              <CheckCircle2 className="w-3 h-3" /> Chosen
            </Badge>
          ) : rec.resolution?.status === 'deferred' ? (
            <span className="inline-flex items-center gap-1">
              <Badge className="text-muted-foreground border-border/40" testid={`hub-resolution-${rec.rec_id}`}>Deferred</Badge>
              <Button size="sm" variant="ghost" onClick={reviveRecFn} disabled={busyC} data-testid={`hub-revive-${rec.rec_id}`}>
                <Undo2 className="w-3.5 h-3.5 mr-1" /> Revive
              </Button>
            </span>
          ) : (rec.conflict_with || []).length > 0 && onCompare ? (
            <Button size="sm" variant="outline" onClick={() => onCompare(rec)} data-testid={`hub-compare-${rec.rec_id}`}>
              <GitCompareArrows className="w-3.5 h-3.5 mr-1" /> Compare & resolve
            </Button>
          ) : null}
        </div>
      )}

      {rankOpen && (
        <div className="mt-2 rounded-lg border border-violet-500/20 bg-violet-500/[0.04] p-2" data-testid={`hub-rank-detail-${rec.rec_id}`}>
          <p className="text-[11px] text-muted-foreground mb-1">{rec.rank?.explanation}</p>
          <div className="space-y-1">
            {(rec.rank?.factors || []).map((f) => (
              <div key={f.key} className="flex items-center gap-2">
                <span className="text-[10px] w-28 flex-shrink-0 text-muted-foreground">{f.label}</span>
                <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-violet-500/70" style={{ width: `${Math.min(100, (f.contribution / 0.3) * 100)}%` }} />
                </div>
                <span className="text-[10px] w-10 text-right text-muted-foreground">{f.contribution.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Options / alternatives */}
      <button onClick={() => setOpen(!open)} className="mt-2 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground" data-testid={`hub-options-toggle-${rec.rec_id}`}>
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />} {rec.options?.length || 0} options (incl. no-action)
      </button>
      {open && (
        <div className="mt-1.5 space-y-1.5" data-testid={`hub-options-${rec.rec_id}`}>
          {(rec.options || []).map((o) => {
            const Icon = OPTION_ICON[o.kind] || Lightbulb;
            return (
              <div key={o.id} className="flex items-start gap-2 text-[11px] rounded-lg border border-border/30 p-2">
                <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-muted-foreground" />
                <div>
                  <span className="font-medium text-foreground">{o.label}</span>
                  <span className="text-muted-foreground"> — {o.consequence}</span>
                  <span className="text-muted-foreground/70"> · impact {o.impact_display} · reversibility {o.reversibility}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Decision action */}
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        {req && excStatus === 'granted' ? (
          <Badge className="text-emerald-700 dark:text-emerald-300 border-emerald-500/40 bg-emerald-500/10" testid={`hub-exception-granted-${rec.rec_id}`}>
            <CheckCircle2 className="w-3 h-3" /> Exception granted{exc?.expiry ? ` · until ${exc.expiry}` : ''}
          </Badge>
        ) : req && excStatus === 'pending' ? (
          <div className="flex items-center gap-2" data-testid={`hub-exception-pending-${rec.rec_id}`}>
            <Badge className="text-amber-700 dark:text-amber-300 border-amber-500/40 bg-amber-500/10">
              <FileWarning className="w-3 h-3" /> Exception pending
            </Badge>
            <Button size="sm" variant="outline" onClick={() => decideExc('granted')} disabled={excBusy} data-testid={`hub-exception-grant-${rec.rec_id}`}>Grant</Button>
            <Button size="sm" variant="ghost" onClick={() => decideExc('denied')} disabled={excBusy} data-testid={`hub-exception-deny-${rec.rec_id}`}>Deny</Button>
          </div>
        ) : req ? (
          <Button size="sm" variant="outline" onClick={() => setExcDialog(true)} data-testid={`hub-exception-request-${rec.rec_id}`}>
            <FileWarning className="w-3.5 h-3.5 mr-1" /> Request exception
          </Button>
        ) : null}

        {approvalId || status === 'pending_approval' || status === 'approved' || status === 'actioned' ? (
          <Button size="sm" variant="outline" onClick={() => navigate(`/admin/recommendation-approvals${approvalId ? `?approval=${approvalId}` : ''}`)} data-testid={`hub-view-approval-${rec.rec_id}`}>
            <ExternalLink className="w-3.5 h-3.5 mr-1" /> View in approvals
          </Button>
        ) : rec.material ? (
          <Button size="sm" onClick={route} disabled={routing} data-testid={`hub-route-${rec.rec_id}`}>
            {routing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />} Route to approval
          </Button>
        ) : !req ? (
          <span className="text-[11px] text-muted-foreground">Advisory — no approval required.</span>
        ) : null}
      </div>

      {/* Outcome tracking + revalidation (Phase C) — for actioned/approved decisions with a measurable signal */}
      {!isContent && (status === 'approved' || status === 'actioned') && (
        <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`hub-outcome-${rec.rec_id}`}>
          {outcome ? (
            <>
              <Badge className={OUTCOME_COLOR[outcome.status] || 'border-border/40 text-muted-foreground'} testid={`hub-outcome-status-${rec.rec_id}`}>
                <Activity className="w-3 h-3" /> Outcome: {OUTCOME_LABEL[outcome.status] || outcome.status}
                {outcome.latest?.delta_display ? ` · ${outcome.latest.delta_display}` : ''}
              </Badge>
              <Button size="sm" variant="outline" onClick={measureNow} disabled={busyC} data-testid={`hub-outcome-measure-${rec.rec_id}`}>
                {busyC ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />} Measure now
              </Button>
              {(outcome.latest?.business_events || []).length > 0 && (
                <span className="text-[11px] text-muted-foreground">
                  Context: {(outcome.latest.business_events || []).map((ev) => ev.name).join(', ')}
                </span>
              )}
            </>
          ) : (
            <Button size="sm" variant="outline" onClick={trackOutcome} disabled={busyC} data-testid={`hub-outcome-track-${rec.rec_id}`}>
              {busyC ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Activity className="w-3.5 h-3.5 mr-1" />} Track outcome
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={revalidate} disabled={busyC} data-testid={`hub-revalidate-${rec.rec_id}`}>
            <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Revalidate
          </Button>
          {reval && <span className="text-[11px] text-muted-foreground" data-testid={`hub-reval-note-${rec.rec_id}`}>{reval.note}</span>}
        </div>
      )}

      {/* Event impact link — attach a business event as the likely cause of the shift (Phase C follow-up) */}
      {!isContent && outcome && (outcome.attributed_event || (outcome.latest?.business_events || []).length > 0) && (
        <div className="mt-1.5 flex items-center gap-2 flex-wrap" data-testid={`hub-attribute-${rec.rec_id}`}>
          {outcome.attributed_event ? (
            <Badge className="text-violet-700 dark:text-violet-300 border-violet-500/30 bg-violet-500/10" testid={`hub-attributed-${rec.rec_id}`}>
              <CalendarClock className="w-3 h-3" /> Likely cause: {outcome.attributed_event.name}
              {outcome.attributed_event.confidence ? ` · ${outcome.attributed_event.confidence} confidence` : ''}
              <button onClick={() => attributeEvent('', 'medium')} disabled={busyC} className="ml-1 hover:text-foreground" data-testid={`hub-attribute-clear-${rec.rec_id}`}>×</button>
            </Badge>
          ) : (
            <>
              <span className="text-[11px] text-muted-foreground">Likely cause:</span>
              <select value={pendingEvent} onChange={(e) => setPendingEvent(e.target.value)}
                className="h-7 rounded-md border border-border/50 bg-background px-2 text-[11px]" data-testid={`hub-attribute-select-${rec.rec_id}`}>
                <option value="">Select an event…</option>
                {(outcome.latest?.business_events || []).map((ev) => <option key={ev.event_id} value={ev.event_id}>{ev.name}</option>)}
              </select>
              <select value={causeConf} onChange={(e) => setCauseConf(e.target.value)}
                className="h-7 rounded-md border border-border/50 bg-background px-2 text-[11px]" data-testid={`hub-cause-confidence-${rec.rec_id}`}>
                <option value="low">Low confidence</option>
                <option value="medium">Medium confidence</option>
                <option value="high">High confidence</option>
              </select>
              <Button size="sm" variant="outline" onClick={markCause} disabled={busyC || !pendingEvent} data-testid={`hub-attribute-mark-${rec.rec_id}`}>Mark cause</Button>
            </>
          )}
        </div>
      )}

      {/* Guardrailed pilot — reversible trial before full rollout (Phase E) */}
      {!isContent && rec.material && (
        <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`hub-pilot-${rec.rec_id}`}>
          {(!pilot || pilot.status === 'rolled_back') ? (
            <Button size="sm" variant="outline" onClick={() => setPilotDialog(true)} disabled={busyC} data-testid={`hub-pilot-start-${rec.rec_id}`}>
              <FlaskConical className="w-3.5 h-3.5 mr-1" /> {pilot?.status === 'rolled_back' ? 'Start new pilot' : 'Start pilot'}
            </Button>
          ) : (
            <>
              <Badge className={PILOT_COLOR[pilot.status] || 'border-border/40 text-muted-foreground'} testid={`hub-pilot-status-${rec.rec_id}`}>
                <FlaskConical className="w-3 h-3" /> Pilot: {PILOT_LABEL[pilot.status] || pilot.status}
                {pilot.latest?.delta_display ? ` · ${pilot.latest.delta_display}` : ''}
              </Badge>
              {pilot.status === 'running' && (
                <>
                  <Button size="sm" variant="outline" onClick={measurePilotFn} disabled={busyC} data-testid={`hub-pilot-measure-${rec.rec_id}`}>
                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Measure pilot
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => decidePilotFn('promote')} disabled={busyC} data-testid={`hub-pilot-promote-${rec.rec_id}`}>
                    <Rocket className="w-3.5 h-3.5 mr-1" /> Promote
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => decidePilotFn('rollback')} disabled={busyC} data-testid={`hub-pilot-rollback-${rec.rec_id}`}>
                    <RotateCcw className="w-3.5 h-3.5 mr-1" /> Roll back
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      )}

      <Dialog open={pilotDialog} onOpenChange={setPilotDialog}>
        <DialogContent className="max-w-md" data-testid={`hub-pilot-dialog-${rec.rec_id}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FlaskConical className="w-4 h-4 text-violet-500" /> Start a pilot</DialogTitle>
            <DialogDescription>A small, reversible trial. Captures a baseline of the target metric now; nothing executes — you can roll it back.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label>Pilot name</Label>
              <Input value={pilotName} onChange={(e) => setPilotName(e.target.value)} placeholder="e.g. LIAB rate pilot — 2 states" data-testid={`hub-pilot-name-${rec.rec_id}`} />
            </div>
            <div>
              <Label>Scope (optional)</Label>
              <Input value={pilotScope} onChange={(e) => setPilotScope(e.target.value)} placeholder="Where/how it's limited" data-testid={`hub-pilot-scope-${rec.rec_id}`} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPilotDialog(false)}>Cancel</Button>
            <Button onClick={doStartPilot} disabled={busyC} data-testid={`hub-pilot-create-${rec.rec_id}`}>Start pilot</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={excDialog} onOpenChange={setExcDialog}>
        <DialogContent className="max-w-md" data-testid={`hub-exception-dialog-${rec.rec_id}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileWarning className="w-4 h-4 text-amber-500" /> Request exception</DialogTitle>
            <DialogDescription>
              "{rec.title}" is a {OBLIGATION_LABEL[rec.obligation]} recommendation and can't be simply dismissed. Record why an exception is warranted; a second approver must grant it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label>Reason</Label>
              <Input value={excReason} onChange={(e) => setExcReason(e.target.value)} placeholder="Why this is safe to defer" data-testid={`hub-exception-reason-${rec.rec_id}`} />
            </div>
            <div>
              <Label>Expiry (optional)</Label>
              <Input type="date" value={excExpiry} onChange={(e) => setExcExpiry(e.target.value)} data-testid={`hub-exception-expiry-${rec.rec_id}`} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExcDialog(false)}>Cancel</Button>
            <Button onClick={submitException} disabled={excBusy} data-testid={`hub-exception-submit-${rec.rec_id}`}>
              {excBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const VIEW_ICON = {
  decisions_required: Inbox, required_actions: AlertTriangle, critical_risks: ShieldCheck,
  opportunities: Lightbulb, pending_approval: Inbox, in_progress: TrendingUp,
  awaiting_outcome: CheckCircle2, measuring_outcome: Activity, content_health: HeartPulse,
  conflicts: Split, all: Briefcase,
};

export default function RecommendationsHubPage() {
  const canWrite = useCan('measures:certify');
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState('');
  const [recs, setRecs] = useState([]);
  const [counts, setCounts] = useState({});
  const [views, setViews] = useState([]);
  const [activeView, setActiveView] = useState('all');
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [autoScan, setAutoScan] = useState(true);
  const [digestCadence, setDigestCadence] = useState('weekly');
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [initDialog, setInitDialog] = useState(false);
  const [initName, setInitName] = useState('');
  const [initDesc, setInitDesc] = useState('');
  const [creatingInit, setCreatingInit] = useState(false);
  const [compareRec, setCompareRec] = useState(null);
  const [chosenId, setChosenId] = useState('');
  const [resolveNote, setResolveNote] = useState('');
  const [resolving, setResolving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [digestWeekday, setDigestWeekday] = useState(0);
  const [digestHour, setDigestHour] = useState(8);
  const [digestTz, setDigestTz] = useState(BROWSER_TZ);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [resOpen, setResOpen] = useState(false);
  const [resData, setResData] = useState([]);
  const [resLoading, setResLoading] = useState(false);
  const [resTotal, setResTotal] = useState(0);
  const [resLoadingMore, setResLoadingMore] = useState(false);

  useEffect(() => {
    api.getSemanticModels().then((r) => setModels(r.models || r || [])).catch(() => {});
    api.getRecommendationsHubOptions().then((r) => setViews(r.views || [])).catch(() => {});
    api.getContentScanSettings().then((s) => setAutoScan(s.enabled !== false)).catch(() => {});
    api.getOutcomeDigestSettings().then((s) => {
      setDigestCadence(s.cadence || 'weekly');
      setDigestWeekday(s.weekday ?? 0);
      setDigestHour(s.hour ?? 8);
      setDigestTz(s.timezone || BROWSER_TZ);
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getRecommendationsHub(modelId);
      setRecs(r.recommendations || []);
      setCounts(r.counts || {});
    } catch (e) {
      toast.error(e.message || 'Could not load recommendations');
    } finally { setLoading(false); }
  }, [modelId]);

  useEffect(() => { load(); }, [load]);

  const scanContent = async () => {
    setScanning(true);
    try {
      const r = await api.scanContent(modelId);
      toast.success(`Content scan complete — ${r.generated} content recommendation${r.generated === 1 ? '' : 's'}.`);
      await load();
      setActiveView('content_health');
    } catch (e) { toast.error(e.message || 'Could not scan content'); }
    finally { setScanning(false); }
  };

  const toggleAutoScan = async () => {
    const next = !autoScan;
    setAutoScan(next);
    try { await api.setContentScanSettings(next); toast.success(`Nightly auto-scan ${next ? 'on' : 'off'}.`); }
    catch (e) { setAutoScan(!next); toast.error(e.message || 'Could not update auto-scan'); }
  };
  const changeDigest = async (cadence) => {
    const prev = digestCadence;
    setDigestCadence(cadence);
    try { await api.setOutcomeDigestSettings({ cadence }); toast.success(`Outcome digest: ${cadence === 'off' ? 'Off' : 'Weekly'}.`); }
    catch (e) { setDigestCadence(prev); toast.error(e.message || 'Could not update digest'); }
  };
  const saveSchedule = async () => {
    setSavingSchedule(true);
    try {
      await api.setOutcomeDigestSettings({ cadence: digestCadence, weekday: digestWeekday, hour: digestHour, timezone: digestTz });
      toast.success('Digest schedule saved.');
    } catch (e) { toast.error(e.message || 'Could not save schedule'); }
    finally { setSavingSchedule(false); }
  };
  const openResHistory = async () => {
    setResOpen(true); setResLoading(true); setResData([]); setResTotal(0);
    try {
      const r = await api.listConflictResolutions({ limit: RES_PAGE, offset: 0 });
      setResData(r.resolutions || []); setResTotal(r.total || 0);
    } catch (e) { toast.error(e.message || 'Could not load history'); }
    finally { setResLoading(false); }
  };
  const loadMoreRes = async () => {
    setResLoadingMore(true);
    try {
      const r = await api.listConflictResolutions({ limit: RES_PAGE, offset: resData.length });
      setResData((p) => [...p, ...(r.resolutions || [])]); setResTotal(r.total || 0);
    } catch (e) { toast.error(e.message || 'Could not load more'); }
    finally { setResLoadingMore(false); }
  };

  const toggleSelect = (recId) => setSelectedIds((p) => (p.includes(recId) ? p.filter((x) => x !== recId) : [...p, recId]));
  const createInitiative = async () => {
    if (!initName.trim()) { toast.error('A name is required'); return; }
    setCreatingInit(true);
    try {
      await api.createInitiative({ name: initName.trim(), description: initDesc.trim(), rec_ids: selectedIds });
      toast.success(`Initiative created with ${selectedIds.length} recommendation${selectedIds.length === 1 ? '' : 's'}.`);
      setInitDialog(false); setInitName(''); setInitDesc(''); setSelectedIds([]); setSelectMode(false);
      await load();
    } catch (e) { toast.error(e.message || 'Could not create initiative'); }
    finally { setCreatingInit(false); }
  };

  const openCompare = (rec) => { setCompareRec(rec); setChosenId(rec.rec_id); setResolveNote(''); };
  const compareGroup = compareRec ? [compareRec, ...recs.filter((r) => (compareRec.conflict_with || []).includes(r.rec_id))] : [];
  const doResolve = async () => {
    if (!chosenId) { toast.error('Pick the winning recommendation'); return; }
    if (!resolveNote.trim()) { toast.error('A rationale is required'); return; }
    setResolving(true);
    try {
      const deferred = compareGroup.map((r) => r.rec_id).filter((id) => id !== chosenId);
      await api.resolveConflict(chosenId, deferred, resolveNote.trim());
      toast.success('Conflict resolved — the chosen recommendation stays live; the rest are deferred.');
      setCompareRec(null);
      await load();
    } catch (e) { toast.error(e.message || 'Could not resolve conflict'); }
    finally { setResolving(false); }
  };

  const openPreview = async () => {
    setPreviewOpen(true); setPreviewLoading(true);
    try { setPreviewData(await api.getOutcomeDigestPreview()); }
    catch (e) { toast.error(e.message || 'Could not load preview'); }
    finally { setPreviewLoading(false); }
  };

  const shown = recs.filter((r) => (r.views || []).includes(activeView));

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="recommendations-hub-page">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Briefcase className="w-6 h-6 text-violet-500" /> Governed Recommendations</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            The governed decision hub. Every recommendation carries its evidence, confidence, alternatives (including no-action)
            and a transparent rank. Advisory only — material actions route to human approval with a full audit trail. Nothing auto-executes.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={modelId} onChange={(e) => setModelId(e.target.value)}
            className="h-9 rounded-md border border-border/50 bg-background px-3 text-sm" data-testid="hub-model-select">
            <option value="">All models</option>
            {models.map((m) => <option key={m.model_id} value={m.model_id}>{m.name || m.model_id}</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={scanContent} disabled={scanning} data-testid="hub-scan-content">
            {scanning ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ScanSearch className="w-3.5 h-3.5 mr-1" />} Scan content
          </Button>
          <Button size="sm" variant="ghost" onClick={openResHistory} data-testid="hub-res-history-btn" title="Past conflict trade-off decisions">
            <History className="w-3.5 h-3.5 mr-1" /> Resolution history
          </Button>
          {canWrite && (
            <>
              <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer select-none" data-testid="hub-autoscan-toggle" title="Run a nightly content-health scan automatically">
                <input type="checkbox" checked={autoScan} onChange={toggleAutoScan} className="accent-violet-500" /> Nightly auto-scan
              </label>
              <select value={digestCadence} onChange={(e) => changeDigest(e.target.value)}
                className="h-9 rounded-md border border-border/50 bg-background px-2 text-[11px]" data-testid="hub-digest-cadence" title="Weekly recap of acted-on recommendations' measured impact">
                <option value="off">Outcome digest: Off</option>
                <option value="weekly">Outcome digest: Weekly</option>
              </select>
              <Button size="sm" variant="outline" onClick={openPreview} data-testid="hub-digest-preview-btn" title="Preview this week's outcome digest">
                <Eye className="w-3.5 h-3.5 mr-1" /> Preview digest
              </Button>
              <Button size="sm" variant={selectMode ? 'default' : 'outline'} onClick={() => { setSelectMode(!selectMode); setSelectedIds([]); }} data-testid="hub-select-toggle">
                <Layers className="w-3.5 h-3.5 mr-1" /> {selectMode ? 'Cancel select' : 'Select'}
              </Button>
            </>
          )}
        </div>
      </div>

      {selectMode && (
        <div className="flex items-center justify-between gap-3 mb-3 rounded-xl border border-violet-500/30 bg-violet-500/[0.05] px-3 py-2" data-testid="hub-select-bar">
          <span className="text-xs text-muted-foreground">{selectedIds.length} selected — tick related recommendations to bundle them.</span>
          <Button size="sm" onClick={() => setInitDialog(true)} disabled={selectedIds.length === 0} data-testid="hub-create-initiative">
            <Layers className="w-3.5 h-3.5 mr-1" /> Group into initiative ({selectedIds.length})
          </Button>
        </div>
      )}

      {/* View pills */}
      <div className="flex flex-wrap gap-1.5 mb-4" data-testid="hub-views">
        {views.map((v) => {
          const Icon = VIEW_ICON[v.key] || Briefcase;
          const on = activeView === v.key;
          const c = counts[v.key] || 0;
          return (
            <button key={v.key} onClick={() => setActiveView(v.key)} title={v.description}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-colors ${on ? 'bg-violet-500/15 border-violet-500/40 text-violet-700 dark:text-violet-300' : 'border-border/40 text-muted-foreground hover:bg-muted/40'}`}
              data-testid={`hub-view-${v.key}`}>
              <Icon className="w-3.5 h-3.5" /> {v.label} <span className="opacity-70">({c})</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
      ) : shown.length === 0 ? (
        <div className="text-sm text-muted-foreground py-16 text-center" data-testid="hub-empty">
          No recommendations in this view. Run a governed diagnosis in AI Explore (e.g. "why did loss ratio increase?") to generate evidence-backed recommendations.
        </div>
      ) : (
        <div className="space-y-3" data-testid="hub-list">
          {shown.map((r) => (
            <RecCard key={r.rec_id} rec={r} selectMode={selectMode}
              selected={selectedIds.includes(r.rec_id)} onToggleSelect={toggleSelect}
              onCompare={openCompare} onResolved={load} />
          ))}
        </div>
      )}

      {/* Create initiative */}
      <Dialog open={initDialog} onOpenChange={setInitDialog}>
        <DialogContent className="max-w-md" data-testid="hub-initiative-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Layers className="w-4 h-4 text-violet-500" /> Group into initiative</DialogTitle>
            <DialogDescription>Bundle {selectedIds.length} recommendation{selectedIds.length === 1 ? '' : 's'} to review, approve and track together.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label>Name</Label>
              <Input value={initName} onChange={(e) => setInitName(e.target.value)} placeholder="e.g. Q3 Liability turnaround" data-testid="hub-initiative-name" />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input value={initDesc} onChange={(e) => setInitDesc(e.target.value)} placeholder="What this initiative is for" data-testid="hub-initiative-desc" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInitDialog(false)}>Cancel</Button>
            <Button onClick={createInitiative} disabled={creatingInit} data-testid="hub-initiative-create">
              {creatingInit ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compare & resolve conflict */}
      <Dialog open={!!compareRec} onOpenChange={(o) => !o && setCompareRec(null)}>
        <DialogContent className="max-w-3xl" data-testid="hub-compare-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><GitCompareArrows className="w-4 h-4 text-amber-500" /> Compare & resolve the trade-off</DialogTitle>
            <DialogDescription>Pick the recommendation to pursue. The others are recorded as deferred (reversible) with your rationale.</DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-3 py-1">
            {compareGroup.map((r) => (
              <label key={r.rec_id} className={`rounded-xl border p-3 cursor-pointer transition-colors ${chosenId === r.rec_id ? 'border-violet-500/50 bg-violet-500/[0.06]' : 'border-border/40 hover:bg-muted/30'}`} data-testid={`hub-compare-option-${r.rec_id}`}>
                <div className="flex items-center gap-2">
                  <input type="radio" name="chosen" checked={chosenId === r.rec_id} onChange={() => setChosenId(r.rec_id)} className="accent-violet-500" data-testid={`hub-compare-pick-${r.rec_id}`} />
                  <span className="font-medium text-sm text-foreground truncate">{r.title}</span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">{r.action}</p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(r.objective_alignment || []).map((o) => (
                    <span key={o.objective_id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] border border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300">
                      <Target className="w-3 h-3" /> {o.name} {o.direction ? `(${o.direction})` : ''}
                    </span>
                  ))}
                  {r.impact?.dollars_display && r.impact.dollars_display !== '—' && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] border border-border/40 text-foreground">{r.impact.points_display} ≈ {r.impact.dollars_display}</span>
                  )}
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] border border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300">Evidence: {EVIDENCE_LABEL[r.evidence_level] || r.evidence_level}</span>
                </div>
              </label>
            ))}
          </div>
          <div>
            <Label>Rationale for the trade-off</Label>
            <Input value={resolveNote} onChange={(e) => setResolveNote(e.target.value)} placeholder="Why this choice wins" data-testid="hub-compare-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompareRec(null)}>Cancel</Button>
            <Button onClick={doResolve} disabled={resolving} data-testid="hub-compare-resolve">
              {resolving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Resolve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Outcome digest preview */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl" data-testid="hub-digest-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="w-4 h-4 text-violet-500" /> Outcome digest preview</DialogTitle>
            <DialogDescription>What this week's steward recap would say right now — not sent, and the weekly timer is untouched.</DialogDescription>
          </DialogHeader>
          {previewLoading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
          ) : previewData ? (
            <div className="py-1">
              <p className="text-sm text-foreground" data-testid="hub-digest-preview-summary">{previewData.summary}</p>
              <p className="text-[11px] text-muted-foreground mt-1">Cadence: {previewData.cadence === 'off' ? 'Off' : 'Weekly'} · {previewData.count} measured this week</p>
              <div className="mt-3 space-y-1">
                {(previewData.rows || []).map((row, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-[11px] rounded-lg border border-border/30 px-2 py-1.5">
                    <span className="truncate text-foreground">{row.title}</span>
                    <span className="flex items-center gap-1.5 flex-shrink-0 text-muted-foreground">
                      <span className="capitalize">{row.status}</span>
                      <span>· {row.movement}</span>
                      {row.attributed && <span>· cause: {row.attributed}</span>}
                    </span>
                  </div>
                ))}
                {(previewData.rows || []).length === 0 && <p className="text-[11px] text-muted-foreground">No measured outcomes in the last 7 days.</p>}
              </div>
              {canWrite && (
                <div className="mt-4 pt-3 border-t border-border/40" data-testid="hub-digest-schedule">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-foreground"><Clock className="w-3.5 h-3.5 text-violet-500" /> Delivery schedule</div>
                  <div className="flex flex-wrap items-end gap-2 mt-2">
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Day</Label>
                      <select value={digestWeekday} onChange={(e) => setDigestWeekday(Number(e.target.value))} className="h-8 rounded-md border border-border/50 bg-background px-2 text-[11px] block mt-0.5" data-testid="hub-digest-weekday">
                        {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Time</Label>
                      <select value={digestHour} onChange={(e) => setDigestHour(Number(e.target.value))} className="h-8 rounded-md border border-border/50 bg-background px-2 text-[11px] block mt-0.5" data-testid="hub-digest-hour">
                        {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{`${String(h).padStart(2, '0')}:00`}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Timezone</Label>
                      <select value={digestTz} onChange={(e) => setDigestTz(e.target.value)} className="h-8 rounded-md border border-border/50 bg-background px-2 text-[11px] block mt-0.5" data-testid="hub-digest-timezone">
                        {TZ_LIST.map((z) => <option key={z} value={z}>{z === BROWSER_TZ ? `${z} (local)` : z}</option>)}
                      </select>
                    </div>
                    <Button size="sm" variant="outline" onClick={saveSchedule} disabled={savingSchedule || digestCadence === 'off'} data-testid="hub-digest-schedule-save">
                      {savingSchedule ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null} Save schedule
                    </Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1.5">
                    {digestCadence === 'off' ? 'Turn the outcome digest on (Weekly) to schedule delivery.' : `Delivers every ${WEEKDAYS[digestWeekday]} at ${String(digestHour).padStart(2, '0')}:00 (${digestTz}).`}
                  </p>
                </div>
              )}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolution history — audit of past conflict trade-offs */}
      <Dialog open={resOpen} onOpenChange={setResOpen}>
        <DialogContent className="max-w-2xl" data-testid="hub-res-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="w-4 h-4 text-violet-500" /> Resolution history</DialogTitle>
            <DialogDescription>Past conflict trade-offs — who chose what, what was deferred, and why.</DialogDescription>
          </DialogHeader>
          {resLoading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
          ) : resData.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center" data-testid="hub-res-history-empty">No conflict trade-offs recorded yet.</p>
          ) : (
            <div className="space-y-2 py-1 max-h-[60vh] overflow-y-auto" data-testid="hub-res-history-list">
              <p className="text-[10px] text-muted-foreground px-0.5" data-testid="hub-res-history-count">Showing {resData.length} of {resTotal}</p>
              {resData.map((r) => (
                <div key={r.resolution_id} className="rounded-xl border border-border/40 p-3" data-testid={`hub-res-item-${r.resolution_id}`}>
                  <div className="flex items-center gap-1.5 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <span className="font-medium text-foreground">Chose: {r.chosen_title}</span>
                  </div>
                  {(r.deferred || []).length > 0 && (
                    <div className="text-[11px] text-muted-foreground mt-1">Deferred: {(r.deferred || []).map((d) => d.title).join('; ')}</div>
                  )}
                  <div className="text-[11px] text-foreground/80 mt-1 italic">“{r.note}”</div>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    {(r.decided_by || {}).name || 'steward'} · {r.decided_at ? new Date(r.decided_at).toLocaleString() : ''}
                  </div>
                </div>
              ))}
              {resData.length < resTotal && (
                <div className="flex items-center justify-center pt-1">
                  <Button size="sm" variant="outline" onClick={loadMoreRes} disabled={resLoadingMore} data-testid="hub-res-history-more">
                    {resLoadingMore ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null} Load {Math.min(RES_PAGE, resTotal - resData.length)} more ({resTotal - resData.length} older)
                  </Button>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setResOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
